/**
 * Indirizzi email degli utenti dell'organizzazione.
 *
 * Servono a non scambiare per interlocutore la scheda di un collega: le email
 * inviate finivano agganciate alla scheda che porta l'indirizzo di chi scrive.
 * Include anche gli utenti disattivati, perche' le loro schede restano in
 * anagrafica e i thread vecchi le citano ancora.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const cache = new Map<string, Set<string>>();
const inFlight = new Map<string, Promise<Set<string>>>();

async function loadInternalEmails(organizationId: string): Promise<Set<string>> {
  const cached = cache.get(organizationId);
  if (cached) return cached;

  const pending = inFlight.get(organizationId);
  if (pending) return pending;

  const promise = (async () => {
    const { data } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('organization_id', organizationId);

    const set = new Set<string>(
      (data || [])
        .map((r: { email: string | null }) => (r.email || '').trim().toLowerCase())
        .filter(Boolean),
    );
    cache.set(organizationId, set);
    inFlight.delete(organizationId);
    return set;
  })();

  inFlight.set(organizationId, promise);
  return promise;
}

const EMPTY: Set<string> = new Set();

export function useInternalEmails(organizationId: string | null | undefined): Set<string> {
  const [emails, setEmails] = useState<Set<string>>(() =>
    (organizationId && cache.get(organizationId)) || EMPTY,
  );

  useEffect(() => {
    if (!organizationId) { setEmails(EMPTY); return; }
    let cancelled = false;
    loadInternalEmails(organizationId).then(set => {
      if (!cancelled) setEmails(set);
    });
    return () => { cancelled = true; };
  }, [organizationId]);

  return emails;
}
